import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useOrder, ServiceOption } from '../../context/OrderContext'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import Stepper from '../../components/Stepper'
import AutocompleteInput from '../../components/AutocompleteInput'
import ImageCarousel from '../../components/ImageCarousel'
import OptionDescription from '../../components/OptionDescription'

const RACKET_BRANDS = [
  'Babolat','Wilson','Head','Yonex','Victor','Prince','Dunlop',
  'Tecnifibre','Li-Ning','Prokennex','Volkl','Artengo','Carlton',
  'Karakal','Oliver','FZ Forza','Ashaway','Apacs','RSL','Adidas',
  'Nike','Donnay','Pacific',
].sort()

/**
 * Page Options dans le tunnel partenaire.
 * Même logique que OptionsPage mais branding partenaire.
 */
const PartnerOptionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { draft, setOption, setRacketInfo, goToStep, canGoToStep } = useOrder()
  const { partner } = usePartnerAuth()

  const [options, setOptions] = useState<ServiceOption[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState<ServiceOption | null>(null)

  const [racketBrand, setRacketBrand] = useState('')
  const [racketDescription, setRacketDescription] = useState('')
  const [racketOwnerName, setRacketOwnerName] = useState('')
  const [tensionVertical, setTensionVertical] = useState<number | ''>('')
  const [tensionHorizontal, setTensionHorizontal] = useState<number | ''>('')

  useEffect(() => {
    if (!draft.isPartnerMode) {
      navigate('/partner/book')
      return
    }
    if (!draft.service) {
      navigate('/partner/book/service')
      return
    }

    const loadOptions = async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from('service_options')
        .select('*')
        .eq('service_id', draft.service?.id)
        .eq('is_active', true)
        .order('label')

      setOptions(error ? [] : (data ?? []))
      setLoading(false)
    }

    goToStep('OPTIONS')
    void loadOptions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleValidate = () => {
    if (!selectedOption) {
      alert('Veuillez sélectionner une option de cordage.')
      return
    }
    if (!racketBrand || !tensionVertical || !tensionHorizontal || !racketOwnerName) {
      alert('Merci de renseigner toutes les informations obligatoires.')
      return
    }

    setOption(selectedOption)
    setRacketInfo({
      brand: racketBrand,
      description: racketDescription || '',
      ownerName: racketOwnerName,
      tensionVertical: Number(tensionVertical),
      tensionHorizontal: Number(tensionHorizontal),
    })

    // En mode partenaire, on saute pickup/delivery → directement au paiement
    navigate('/partner/book/payment')
  }

  if (loading) return <p>Chargement des options…</p>

  return (
    <div className="card">
      <Stepper />

      {/* Bannière partenaire */}
      {partner && (
        <div
          data-testid="partner-banner"
          style={{
            background: `${partner.color_code}22`,
            border: `1px solid ${partner.color_code}`,
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <span style={{ fontWeight: 600, color: partner.color_code }}>
            🏢 Réservation {partner.name}
          </span>
          <span style={{ fontSize: '.85rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
            📍 {draft.partnerAddress}
          </span>
        </div>
      )}

      <div className="card-header">
        <h1 className="card-title">
          Options pour "{draft.service?.name}"
        </h1>
      </div>

      {/* Options */}
      <div className="grid-2" style={{ marginTop: '1rem' }}>
        {options.length === 0 && (
          <p style={{ color: '#9ca3af' }}>Aucune option disponible.</p>
        )}
        {options.map(opt => (
          <button
            key={opt.id}
            type="button"
            data-testid={`option-button-${opt.id}`}
            className={`card ${selectedOption?.id === opt.id ? 'card--active' : ''}`}
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              border: selectedOption?.id === opt.id
                ? '2px solid #22c55e'
                : '1px solid rgba(148,163,184,0.3)',
              padding: 0,
              overflow: 'hidden',
            }}
            onClick={() => setSelectedOption(opt)}
          >
            {opt.image_urls && opt.image_urls.length > 0 && (
              <div style={{ padding: '0.5rem 0.5rem 0' }}>
                <ImageCarousel images={opt.image_urls} alt={opt.label} height={100} />
              </div>
            )}
            <div style={{ padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{opt.label}</strong>
                <span style={{ color: '#22c55e' }}>
                  {opt.price_delta > 0 ? `+${opt.price_delta.toFixed(2)}` : opt.price_delta.toFixed(2)} CHF
                </span>
              </div>
              {opt.description && (
                <OptionDescription
                  description={opt.description}
                  testId={`option-description-${opt.id}`}
                />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Infos raquette */}
      <div style={{ marginTop: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Informations raquette
        </h2>

        <div style={{ marginBottom: '1rem' }}>
          <label className="label">Nom du propriétaire *</label>
          <input
            className="input"
            value={racketOwnerName}
            onChange={(e) => setRacketOwnerName(e.target.value)}
            placeholder="Ex: Jean Dupont"
            data-testid="racket-owner-name-input"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label className="label">Marque *</label>
          <AutocompleteInput
            suggestions={RACKET_BRANDS}
            value={racketBrand}
            onChange={setRacketBrand}
            placeholder="Ex: Babolat"
            dataTestId="racket-brand-input"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label className="label">Description (optionnel)</label>
          <input
            className="input"
            value={racketDescription}
            onChange={(e) => setRacketDescription(e.target.value)}
            placeholder="Ex: Pure Drive 2024"
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <label className="label">Tension verticale (kg) *</label>
            <input
              className="input"
              type="number"
              min={10}
              max={40}
              value={tensionVertical}
              onChange={(e) => setTensionVertical(Number(e.target.value) || '')}
              placeholder="Ex: 25"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Tension horizontale (kg) *</label>
            <input
              className="input"
              type="number"
              min={10}
              max={40}
              value={tensionHorizontal}
              onChange={(e) => setTensionHorizontal(Number(e.target.value) || '')}
              placeholder="Ex: 23"
            />
          </div>
        </div>
      </div>

      {/* Info: pas de pickup/delivery en mode partenaire */}
      <div
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem 1rem',
          background: 'rgba(34, 197, 94, 0.1)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          fontSize: '.9rem',
        }}
        data-testid="partner-transport-info"
      >
        ✅ <strong>Prise en charge et livraison gratuites</strong> à l'adresse du partenaire
        <br />
        <span style={{ color: '#9ca3af' }}>📍 {draft.partnerAddress}</span>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleValidate}
        style={{ marginTop: '1.5rem' }}
        data-testid="partner-options-validate"
      >
        Continuer vers le paiement
      </button>
    </div>
  )
}

export default PartnerOptionsPage
